import { useContext, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import HeadlessDialog from "@components/Dialog";
import ScrollableModalBody from "@components/ScrollableModalBody";
import { IFacilityDTO } from "@interfaces/IFacilityDTO";
import { SnackbarContext } from "@components/constant_components/providers/SnackbarProvider";
import Button from "@components/Button";
import FormikInput from "@components/form/FormikInput";
import FacilityService from "@service/FacilityService";
import Spinner from "@components/Spinner";

interface IFacilityEditProps {
  id: number;
  selectedFacility: IFacilityDTO | null;
  plantId: number;
  plantName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface IFacilityForm {
  type: string;
  invoice_code: string;
  location_code: string;
}

const facilitySchema = Yup.object().shape({
  type: Yup.string()
    .required("Type is required")
    .max(50, "Type must not exceed 50 characters"),
  invoice_code: Yup.string().max(10, "Invoice code must not exceed 10 characters").nullable(),
  location_code: Yup.string()
    .max(10, "Location code must not exceed 10 characters")
    .nullable(),
});

export function FacilityCreateEdit({
  id,
  selectedFacility,
  plantId,
  plantName,
  isOpen,
  onClose,
}: IFacilityEditProps): JSX.Element {
  const { triggerSnackbar } = useContext(SnackbarContext);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = id > 0 && selectedFacility !== null;

  const initialValues: IFacilityForm = isEditMode
    ? {
        type: selectedFacility!.type ?? "",
        invoice_code: selectedFacility!.invoice_code ?? "",
        location_code: selectedFacility!.location_code ?? "",
      }
    : {
        type: "",
        invoice_code: "",
        location_code: "",
      };

  const handleSubmit = async (values: IFacilityForm) => {
    setIsSubmitting(true);
    try {
      const payload = { ...values, plant_id: plantId };
      if (isEditMode) {
        await FacilityService.updateFacility({ id, ...payload });
        triggerSnackbar("Facility updated successfully", "success");
      } else {
        await FacilityService.createFacility(payload);
        triggerSnackbar("Facility created successfully", "success");
      }
      onClose();
    } catch (error: any) {
      console.error("Error saving facility:", error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.message ||
        "An error occurred while saving the facility";
      triggerSnackbar(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <HeadlessDialog
      isOpen={isOpen}
      setIsOpen={onClose}
      onClose={onClose}
      title={isEditMode ? "Edit Facility" : "Create Facility"}
      description=""
      buttonText="Close"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={facilitySchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {() => (
          <Form>
            <ScrollableModalBody>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <div className="md:tw-col-span-2 tw-bg-gray-50 tw-border tw-rounded tw-p-3 tw-text-sm">
                  <span className="tw-font-semibold tw-text-gray-700">
                    Plant:
                  </span>{" "}
                  <span className="tw-text-gray-900">{plantName}</span>
                </div>
                <div className="md:tw-col-span-2">
                  <Field
                    as={FormikInput}
                    label="Type"
                    name="type"
                    type="text"
                    placeholder="e.g., Warehouse, Lumber Yard"
                    required
                  />
                </div>
                <Field
                  as={FormikInput}
                  label="Invoice Code"
                  name="invoice_code"
                  type="text"
                  placeholder="Invoice code"
                  maxLength={10}
                />
                <Field
                  as={FormikInput}
                  label="Location Code"
                  name="location_code"
                  type="text"
                  placeholder="Location code"
                  maxLength={10}
                />
              </div>
            </ScrollableModalBody>

            <div className="tw-flex tw-justify-end tw-gap-2 tw-p-4 tw-border-t">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="tw-px-4 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded hover:tw-bg-blue-700"
              >
                {isSubmitting ? (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <Spinner className="tw-w-4 tw-h-4" />
                    <span>Saving...</span>
                  </div>
                ) : isEditMode ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </HeadlessDialog>
  );
}
